import React, { useEffect, useState, useRef } from "react";
import {
    GoogleMap,
    InfoWindow
} from "@react-google-maps/api";
import { localImages } from "../../common/localImages";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";
import { UnifiedTrackingService } from "../../service/unifiedTracking.service";
import ModernMarker from "../LiveTracking/ModernMarker"; // Import ModernMarker

// Map setup
const containerStyle = {
    width: "100%",
    height: "100%",
};

const center = {
    lat: 10.956724,
    lng: 78.081329,
};

const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    gestureHandling: 'greedy',
    minZoom: 10,
    zoomAnimation: true,
    mapId: 'motosop-trip-map',
};

interface SimpleTripMapProps {
    tripId?: string;
    showMap?: boolean;
    onMapReady?: () => void;
    trackingUrl?: string; // LivePin tracking URL
}

const SimpleTripMap: React.FC<SimpleTripMapProps> = ({
    tripId,
    showMap = true,
    onMapReady,
    trackingUrl
}) => {
    const [latestData, setLatestData] = useState<any>(null);
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<string>("disconnected");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mapRef = useRef<google.maps.Map | null>(null);
    const initialLoadRef = useRef<boolean>(true);

    // Use centralized Google Maps context
    const { isLoaded, setMap } = useGoogleMaps();

    const onMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        setMap(map);
        onMapReady?.();
    };

    // LivePin tracking - Frontend only, no backend calls
    useEffect(() => {
        console.log("🔍 SimpleTripMap useEffect - trackingUrl:", trackingUrl);
        console.log("🔍 SimpleTripMap useEffect - trackingUrl type:", typeof trackingUrl);

        if (!trackingUrl) {
            console.log("🔍 SimpleTripMap - No tracking URL, setting error");
            setError("No tracking URL available");
            setIsLoading(false); // Clear loading state
            setConnectionStatus("disconnected");
            return;
        }

        // Clear any previous error when trackingUrl becomes available
        console.log("🔍 SimpleTripMap - Tracking URL available, clearing error");
        setError(null);

        setConnectionStatus("connecting");
        setIsLoading(true);

        const unifiedService = new UnifiedTrackingService();
        let pollingInterval: NodeJS.Timeout;

        const startTracking = async () => {
            try {
                console.log('🚀 Starting unified tracking with URL:', trackingUrl);
                // Fetch initial data
                const vehicleDataArray = await unifiedService.fetchVehicleData(trackingUrl);
                if (vehicleDataArray && vehicleDataArray.length > 0) {
                    const vehicleData = vehicleDataArray[0]; // Use first vehicle
                    console.log('✅ Unified tracking data received:', vehicleData);
                    setLatestData(vehicleData);
                    setConnectionStatus("connected");
                    setIsLoading(false);

                    if (mapRef.current) {
                        mapRef.current.panTo({ lat: vehicleData.latitude, lng: vehicleData.longitude });
                    }
                } else {
                    setError("No vehicle data available");
                    setConnectionStatus("error");
                    setIsLoading(false);
                }

                // Start polling for updates every 10 seconds
                pollingInterval = setInterval(async () => {
                    try {
                        const updatedDataArray = await unifiedService.fetchVehicleData(trackingUrl);
                        if (updatedDataArray && updatedDataArray.length > 0) {
                            const updatedData = updatedDataArray[0];
                            console.log('🔄 Unified tracking data updated:', updatedData);
                            setLatestData(updatedData);
                        }
                    } catch (error) {
                        console.error("Error fetching unified tracking data:", error);
                        setConnectionStatus("error");
                    }
                }, 60000); // Poll every 1 minute

            } catch (error) {
                console.error("Error starting unified tracking:", error);
                setError(`Failed to start tracking: ${error}`);
                setConnectionStatus("error");
                setIsLoading(false);
            }
        };

        startTracking();

        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
            setConnectionStatus("disconnected");
        };
    }, [trackingUrl, tripId]);

    const formatTimestamp = (timestamp: number): string => {
        return new Date(timestamp).toLocaleTimeString();
    };

    if (!showMap) {
        return (
            <div className="w-full h-full bg-blue-50 flex items-center justify-center text-blue-600 text-lg font-semibold">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <div>Loading trip map...</div>
                </div>
            </div>
        );
    }

    if (isLoading && !error) {
        return (
            <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-600 text-lg font-semibold">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-2"></div>
                    <div>Loading trip data...</div>
                </div>
            </div>
        );
    }

    // Don't show demo mode - let the component use the hardcoded tracking URL and show the map

    if (error) {
        return (
            <div className="w-full h-full bg-red-50 flex items-center justify-center text-red-600 text-lg font-semibold">
                <div className="text-center">
                    <div className="text-red-500 text-4xl mb-2">⚠️</div>
                    <div>{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full relative rounded-lg shadow-lg border border-gray-200">
            {isLoaded && (
                <GoogleMap
                    key={`trip-map-${tripId}-${latestData?.latitude}-${latestData?.longitude}`}
                    mapContainerStyle={containerStyle}
                    center={latestData ? { lat: latestData.latitude, lng: latestData.longitude } : center}
                    zoom={18}
                    options={mapOptions}
                    onLoad={onMapLoad}
                >
                    {/* Vehicle Marker */}
                    {latestData && (
                        <>
                            <ModernMarker
                                position={{ lat: latestData.latitude, lng: latestData.longitude }}
                                title={`Vehicle: ${latestData.vehicle_data?.vehicle_no || 'Unknown'}`}
                                icon={localImages.locationPin}
                                onMouseOver={() => setShowInfoWindow(true)}
                                onMouseOut={() => setShowInfoWindow(false)}
                                map={mapRef.current}
                            />

                            {/* Info Window */}
                            {showInfoWindow && (
                                <InfoWindow
                                    position={{ lat: latestData.latitude, lng: latestData.longitude }}
                                    onCloseClick={() => setShowInfoWindow(false)}
                                >
                                    <div className="p-2 text-sm space-y-1">
                                        <div className="font-semibold text-gray-700">
                                            {latestData.vehicle_data?.vehicle_no ?? "Unknown Vehicle"}
                                        </div>
                                        <div>Speed: {latestData.speed} km/h</div>
                                        <div>Status: {latestData.vehicle_status}</div>
                                        <div>Battery: {latestData.battery_percent}%</div>
                                        <div>Time: {formatTimestamp(latestData.timestamp)}</div>
                                        <div className="text-xs text-gray-500">
                                            {latestData.latitude.toFixed(6)}, {latestData.longitude.toFixed(6)}
                                        </div>
                                        {latestData.address && (
                                            <div className="text-xs text-gray-500">{latestData.address}</div>
                                        )}
                                    </div>
                                </InfoWindow>
                            )}
                        </>
                    )}
                </GoogleMap>
            )}

            {/* Merged Info Panel */}
            {latestData && (
                <div className="absolute top-4 left-4 bg-white bg-opacity-90 p-4 rounded-lg shadow-md w-64 text-sm space-y-1">
                    <div className="font-semibold text-gray-700 mb-2">Live Tracking</div>

                    {/* Vehicle Info */}
                    <div className="font-medium">{latestData.vehicle_data?.vehicle_no || 'Unknown Vehicle'}</div>
                    <div className="text-xs">Speed: {latestData.speed} km/h</div>
                    <div className="text-xs">Status: {latestData.vehicle_status}</div>
                    <div className="text-xs">Battery: {latestData.battery_percent}%</div>

                    {/* Connection status indicator */}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                        <span>Connection: </span>
                        <span className={`text-xs font-bold px-2 py-1 rounded-md ${connectionStatus === "connected"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                            }`}>
                            {connectionStatus.toUpperCase()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimpleTripMap;
